import { Substitution } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import type { TFunction } from 'i18next';
import moment from 'moment-timezone';
import {
  SubstitutionDay,
  SubstitutionDTO,
  SubstitutionEntry,
  SubstitutionMotive,
} from './SubstitutionWidget.interface';

type SubstitutionInput = SubstitutionDTO | Substitution;

/**
 * Transforms a SubstitutionDTO from the API to a SubstitutionEntry for display
 */
export const transformSubstitutionDTO = (
  dto: SubstitutionInput,
  index: number,
): SubstitutionEntry | null => {
  // Early return if substitutionLesson is null (should not happen but schema allows it)
  if (!dto.substitutionLesson) {
    return null;
  }

  const startMoment = moment(dto.substitutionLesson.from);
  const endMoment = moment(dto.substitutionLesson.to);

  const hasRoomChange =
    dto.originLesson &&
    dto.originLesson.room !== dto.substitutionLesson.room &&
    dto.originLesson.room &&
    dto.substitutionLesson.room;

  const hasTeacherChange =
    dto.originLesson &&
    dto.originLesson.teacher !== dto.substitutionLesson.teacher &&
    dto.originLesson.teacher &&
    dto.substitutionLesson.teacher;

  // Build motives array - include exam if exam is true
  const motives: SubstitutionMotive[] = [...((dto.motive ?? []) as SubstitutionMotive[])];
  if (dto.exam && !motives.includes('Exam')) {
    motives.push('Exam');
  }
  if (hasRoomChange && !motives.includes('RoomExchange')) {
    motives.push('RoomExchange');
  }

  return {
    id: `substitution-${index}-${startMoment.format('YYYY-MM-DD-HH-mm')}`,
    period: String(dto.cardinalNumber ?? 0),
    startTime: startMoment.format('HH:mm'),
    endTime: endMoment.format('HH:mm'),
    subject: `${dto.substitutionLesson.title ?? ''} ${(dto.substitutionLesson.classes ?? []).join(
      ', ',
    )}`.trim(),
    className: (dto.substitutionLesson.classes ?? [])[0] || undefined,
    room: dto.substitutionLesson.room ?? undefined,
    originalRoom: hasRoomChange ? dto.originLesson?.room ?? undefined : undefined,
    teacher: dto.substitutionLesson.teacher ?? undefined,
    originalTeacher: hasTeacherChange ? dto.originLesson?.teacher ?? undefined : undefined,
    motives,
    isExam: dto.exam ?? false,
    remark: dto.remark ?? undefined,
    borderColor: dto.substitutionLesson.color ?? undefined,
  };
};

/**
 * Groups substitution DTOs by day and transforms them for display
 */
export const groupSubstitutionsByDay = (
  dtos: SubstitutionInput[],
  t: TFunction,
): SubstitutionDay[] => {
  const today = moment().startOf('day');
  const tomorrow = moment().add(1, 'day').startOf('day');

  // Group by date
  const grouped = new Map<string, SubstitutionInput[]>();

  for (const dto of dtos) {
    // Skip entries without substitutionLesson
    if (!dto.substitutionLesson) {
      continue;
    }
    const dateKey = moment(dto.substitutionLesson.from).format('YYYY-MM-DD');
    const existing = grouped.get(dateKey) || [];
    existing.push(dto);
    grouped.set(dateKey, existing);
  }

  // Transform to SubstitutionDay array
  const days: SubstitutionDay[] = [];

  grouped.forEach((dayDtos: SubstitutionInput[], dateKey: string) => {
    const dateMoment = moment(dateKey);
    const date = dateMoment.toDate();

    // Create label based on relative date
    let label: string;
    if (dateMoment.isSame(today, 'day')) {
      label = t('dashboard:substitution:today', {
        defaultValue: 'Heute, {{date}}',
        date: dateMoment.format('dd. DD.MM.'),
      });
    } else if (dateMoment.isSame(tomorrow, 'day')) {
      label = t('dashboard:substitution:tomorrow', {
        defaultValue: 'Morgen, {{date}}',
        date: dateMoment.format('dd. DD.MM.'),
      });
    } else {
      label = dateMoment.format('dddd, DD.MM.');
    }

    // Sort entries by period and transform
    const sortedDtos = dayDtos.sort(
      (a: SubstitutionInput, b: SubstitutionInput) =>
        (a.cardinalNumber ?? 0) - (b.cardinalNumber ?? 0),
    );
    const entries = sortedDtos
      .map((dto: SubstitutionInput, index: number) => transformSubstitutionDTO(dto, index))
      .filter((entry): entry is SubstitutionEntry => entry !== null);

    days.push({ date, label, entries });
  });

  // Sort days by date
  return days.sort((a: SubstitutionDay, b: SubstitutionDay) => a.date.getTime() - b.date.getTime());
};

/**
 * Formats a date for the "Last updated" display
 */
export const formatLastUpdated = (date: Date): string => {
  return moment(date).format('DD.MM.YY HH:mm [Uhr]');
};
